import { lecternZone } from './framing.js';
import { MODEL_DISTANCE, MODEL_SLUG, modelSlugOf } from './models.js';
import { STAGE, STAGE_ADR032 } from './stage.js';
import { MOTION_LIMITS } from './motion-limits.js';
import { SHAPE_LIMITS, SHAPE_PARTS_CAP_LINE } from './shape-limits.js';
import { openSpace } from './world-package/scenery.js';
import { CODE_WORLD_CATALOGUE, worldIn, } from './world-catalogue.js';
/**
 * What the headset can build, in words a language model can plan a lesson with.
 *
 * The lesson generator (docs/superpowers/specs/2026-09-25-lesson-generator-design.md)
 * puts this in every prompt. It is kept honest by two tests: catalogue.test.ts
 * here checks it against the schema, and apps/headset's catalogue.test.ts
 * checks it against the builders the headset registers. A model told about
 * something the headset cannot draw writes a lesson that shows nothing.
 */
/**
 * The lectern's place, as the "Tekstpaneel vrij van modellen" check measures
 * it (framing.ts), to the 10 cm outward: near rounded down, the rest up.
 */
const ZONE = (() => {
    const zone = lecternZone();
    const down = (n) => (Math.floor(n * 10 + 1e-6) / 10).toFixed(1);
    const up = (n) => (Math.ceil(n * 10 - 1e-6) / 10).toFixed(1);
    return { near: down(zone.near), far: up(zone.far), width: up(zone.width), top: up(zone.top) };
})();
/** The studio as a place, for the list a design chooses from. */
const STUDIO_PLACE = 'A calm, neutral room built in code: a pale floor at y = 0 that fades into a soft sky, lit evenly from above. For VR lessons built from shapes.';
/**
 * Where things go in any VR scene: the pupil, the view and the panel, as the
 * framing and lectern checks judge every VR lesson. For the lesson, not the design.
 */
const VIEW_RULES = 'The pupil starts at the origin with eyes at 1.6 m, looking towards -z. Prefer `view: {look: id}`: the engine chooses where the pupil stands so the subject fills about a third of the view above the panel, and swings the panel aside if it must. ' +
    `Keep the panel’s place free: it stands ${ZONE.near} to ${ZONE.far} m in front of the pupil, ${ZONE.width} m wide and up to ${ZONE.top} m high, so put no model lower than ${ZONE.top} m there.` +
    ' With a fixed `pos`, keep the subject in front of the pupil and at least 1 m away.';
/** A scene with a floor: things stand on it. */
const PLINTH_RULE = 'Put a plinth under a thing on the floor and mark that part `"stand": true`, rather than letting things float.';
/** Where things go in the studio, which holds in a world too. */
const STUDIO_RULES = `${VIEW_RULES} ${PLINTH_RULE}`;
/** A world of the catalogue as a scene the generator can choose (or, for a placement world, cannot). */
const worldScene = (world) => ({
    id: `world:${world.slug}`,
    mode: 'vr',
    generatable: world.generatable,
    description: world.description,
    teacher: world.teacher,
});
/** The scenes that are not worlds, in the catalogue's order: the worlds come after the third. */
const FIRST_SCENES = [
    {
        id: 'ar-room',
        mode: 'ar',
        generatable: true,
        description: 'Passthrough: the pupil sees their own classroom and the lesson places objects in it. Objects arrive in front of the pupil and stand on the table or floor beneath them.',
        teacher: {
            title: 'Uw eigen lokaal (AR)',
            text: 'De leerling ziet de klas door de headset heen; de les zet modellen op tafel of de vloer. Alleen op Quest 3 en 3S.',
        },
    },
    {
        id: 'procedural:solar-system',
        mode: 'vr',
        generatable: true,
        description: 'A starfield around the pupil, for lessons in space. Planets, moons and stars are placed with `pos` or orbit a `parent`.',
        teacher: {
            title: 'Zonnestelsel',
            text: 'Tussen de sterren, voor planeten en manen.',
        },
    },
    {
        id: 'studio',
        mode: 'vr',
        generatable: true,
        description: `${STUDIO_PLACE} ${STUDIO_RULES}`,
        summary: STUDIO_PLACE,
        teacher: {
            title: 'Studio',
            text: 'Een rustige, lege ruimte voor een model dat wij bouwen, zoals een vulkaan of een cel.',
        },
    },
];
const LAST_SCENES = [
    {
        id: 'pano360',
        mode: 'vr',
        // ADR-036: filled, pointed and checked by the platform before release.
        generatable: true,
        description: 'A real place in a 360° photo all around the pupil, with a flat stage of 6 m on its ground. For a place that cannot be built: a real landscape, a building, a city, the school’s own surroundings. The photo is far away: models and panels stand on the stage in front of it.',
        summary: "A 360° photo of a real place around a flat stage: a photo the platform finds, or the school's own photo from the brief.",
        teacher: {
            title: 'Echte plek (360°-foto)',
            text: "Een foto rondom de leerling van een echte plek: een landschap, een gebouw, een stad. Wij zoeken de foto, of u voegt bij uw lesidee eigen 360°-foto's van de school of de omgeving toe (maximaal vier, zonder herkenbare personen).",
        },
    },
    {
        id: 'gltf',
        mode: 'vr',
        generatable: false,
        description: 'A 3D model as the whole environment. Needs an uploaded model, so it is not available to a generated lesson.',
    },
];
/**
 * Every scene, the worlds of `worlds` (default the code worlds) between the studio and the 360° photo:
 * a package with a new slug is a scene a design can choose, as a code world is.
 */
export function scenesOf(worlds = CODE_WORLD_CATALOGUE) {
    return [...FIRST_SCENES, ...worlds.map(worldScene), ...LAST_SCENES];
}
/** The code worlds' scenes: the catalogue as it was before packages. */
export const SCENES = scenesOf();
/**
 * How a lesson asks for its 360° photos (ADR-036), said with the chosen
 * pano360 scene and in the whole catalogue while pano360 is generatable: the
 * model writes the wish or the school's photo number, never the photo, and
 * a direction as what to look for; the platform fills and points them.
 */
export const PANO_PARAGRAPH = [
    'Write `scene: {type: "pano360", want, search}` (want: which place and view, in Dutch; search: 1–6 English words), or `{type: "pano360", brief: n}` for the school’s photo n from the brief.',
    'A step may change the photo with `pano: {…}` of the same form; at most 4 photos, each change a teleport.',
    'The photo cannot be pointed at, except by a hotspot `{find: "de Vesuvius", title, text}`: find says in Dutch what to look for, and the platform points it.',
    'Ask for places and things, never people. The stage rules of a world hold.',
].join(' ');
/**
 * PANO_PARAGRAPH for an echte_plek lesson ("Wat wil je maken?" plan 2): the brief's 360° place with its
 * named points, which a hotspot names and the platform points. Only with the catalogue's `placePoints`.
 * Pieter, 22 Oct 2026 (final review I1): every Onderzoeken step looks at a point, the lesson visits each.
 */
export const PLACE_POINTS_PARAGRAPH = [
    'The lesson is set in this 360° place: write `scene: {"type": "pano360"}` (`{"type": "pano360", "brief": 1}` for the school\'s photo), never `want`, `search` or a step `pano`.',
    'Its points of interest are listed under the brief; unlike the rest of the photo and the scenery the guidelines speak of, they can be pointed at: point at one with a hotspot `{point: "<its name>", title, text}`, never `find`.',
    'Every Onderzoeken step has at least one hotspot on a point, and the lesson visits every point at least once; within a step each point is named once, and a point may come back in a later round with a new question that goes deeper.',
].join(' ');
/** The world stage's last sentence, which an echte_plek lesson's scenes leave out: its points can be pointed at. */
const SCENERY_UNPOINTED = ' Scenery cannot be pointed at.';
/** ADR-032's words, frozen: the prompts before v7 carry them byte for byte. */
const WORLD_STAGE_ADR032 = 'A world is a real-looking place around a flat stage: a circle of 6 m radius around the origin, where every object and every place the pupil stands must be; y = 0 is the ground and nothing goes below it. Beyond it is scenery that cannot be pointed at.';
const WORLD_CHOICE = 'Choose the world that fits the subject, the studio when no place fits.';
const WORLD_FEATURES = 'A step faces a feature with `view: {look: "wereld:vulkaan"}` or `focus` (the pupil turns, staying on the stage) and sets its state by the bare name, `world: {vulkaan: "uitbarsting"}`, kept until changed.';
const n = (value) => String(value);
/**
 * What every `world:` scene shares, said once after the scene list (ADR-032, ADR-061): the stage the
 * lesson happens on (worlds.ts), from the numbers of `stage`. ADR-032's stage gets its words of before.
 */
export function worldStage(stage = STAGE) {
    if (stage === STAGE_ADR032)
        return WORLD_STAGE_ADR032;
    return `A world is a real-looking place close round a small flat platform: the pupil stands within ${n(stage.stand)} m of the origin, all else within ${n(stage.radius)} m and out of the scenery, which may start ${n(stage.clear)} m out and hang over it from ${n(stage.clearHeight)} m up. y = 0 is the floor; nothing goes below it. Scenery cannot be pointed at.`;
}
/** The worlds paragraph for `stage`: the stage, the choice, and the studio's rules, which hold in a world too. */
export function worldsParagraph(stage = STAGE) {
    return [
        worldStage(stage),
        WORLD_CHOICE,
        "The studio's rules hold here too.",
        WORLD_FEATURES,
    ].join(' ');
}
/** ADR-032's worlds paragraph (the prompts before v7). */
export const WORLDS_PARAGRAPH = worldsParagraph(STAGE_ADR032);
/** How a library model stands on `stage` (the model entry's third sentence). */
export function libraryModelPlacement(stage = STAGE) {
    return stage === STAGE_ADR032
        ? `A big model (over ${MODEL_DISTANCE.smallHeight} m high or ${MODEL_DISTANCE.smallLength} m long) stands off the stage: its whole footprint at least ${stage.modelClear} m from the stage centre and ${stage.modelEyes} m from the pupil, at every step and along every walk. A small one, or any that "beweegt niet" (a skeleton), may stand like a shape, ${MODEL_DISTANCE.smallEyes} m from the eyes.`
        : `A big model (over ${MODEL_DISTANCE.smallHeight} m high or ${MODEL_DISTANCE.smallLength} m long) stands off the platform, in the open: its whole footprint at least ${stage.modelClear} m from the centre and ${stage.modelEyes} m from the pupil, out of the scenery, at every step and along every walk. A small one, or any that "beweegt niet" (a skeleton), may stand like a shape, ${MODEL_DISTANCE.smallEyes} m from the eyes.`;
}
export const OBJECT_KINDS = [
    {
        namespace: 'bone',
        names: [
            'skull',
            'spine',
            'ribs',
            'pelvis',
            'shoulder',
            'humerus',
            'forearm',
            'hand',
            'femur',
            'tibia',
            'foot',
        ],
        // AR only: the skeleton lesson and its assembly were built and tested in
        // AR, and ADR-030 keeps it that way now that VR has the studio too.
        modes: ['ar'],
        description: 'Parts of a human skeleton, white, built to adult proportions (1.70 m tall). `pos` is where the part belongs in the standing skeleton, in metres from its feet. The paired parts (shoulder, humerus, forearm, hand, femur, tibia, foot) are drawn as the right-hand one and mirrored when x < 0, so a whole skeleton uses each paired name twice under different object ids. With the `assemble` task the parts start on a tray and the pupil puts them in place.',
    },
    {
        namespace: 'planet',
        names: ['earth', 'mars', 'jupiter', 'saturn'],
        modes: ['vr'],
        description: 'A textured planet. `radius` in metres, `spin` in radians per second, `tilt` in radians, `ring` as inner and outer radius in multiples of the planet radius (Saturn), `orbit` in metres around a `parent`. Earth has clouds and an atmosphere.',
    },
    {
        namespace: 'moon',
        names: 'any',
        modes: ['vr'],
        description: 'A grey cratered moon. Usually given a `parent` planet and an `orbit`.',
    },
    {
        namespace: 'star',
        names: 'any',
        modes: ['vr'],
        description: 'A glowing sun that lights the scene. One per lesson.',
    },
    {
        namespace: 'shape',
        names: 'any',
        modes: ['vr', 'ar'],
        description: [
            'Anything else, built from parts: the name is free (`shape:volcano`, `shape:cell`) and the object gets a `parts` list. Compose something recognisable from few parts.',
            'A part is a primitive `{type, size, pos?, rot?, color, opacity?, stand?, finish?}` with type `box` (size [w, h, d]), `sphere` ([r]), `cylinder` ([rTop, rBottom, h]), `cone` ([r, h], point up), `torus` ([r, tube]), `capsule` ([r, length]) or `plane` ([w, h]);',
            'or `{type: "lathe", points: [[r, y], …], pos?, rot?, color, opacity?, stand?, finish?}`, an outline turned about the y axis, bottom to top (a vase, a mountain, a flask);',
            'or `{type: "extrude", outline: [[x, y], …], depth, pos?, rot?, color, opacity?, stand?, finish?}`, an outline pushed out along z and centred on its pos (a cross-section, an arrow);',
            'or `{type: "group", parts: […], pos?, rot?, scale?}` to move, turn or scale parts together.',
            `Metres; \`pos\` is from the object's \`pos\` (its middle) or from the group; \`rot\` is degrees [x, y, z]; \`color\` is "#rrggbb"; \`opacity\` 0.1–1 lets what is inside show through; \`finish\` is "matte" (the default), "gloss" (wet or polished) or "glow" (hot, or giving light). Flat colours with a finish, no textures. Put a plinth under a thing on the floor and mark that part \`"stand": true\`: the engine frames the thing without it.`,
            `Limits: ${SHAPE_LIMITS.partsPerObject} parts per object in total, groups inside groups but no deeper (${SHAPE_LIMITS.depth} levels), ${SHAPE_LIMITS.pointsPerProfile} points per outline, every size ${SHAPE_LIMITS.sizeMin}–${SHAPE_LIMITS.sizeMax} m, ${SHAPE_LIMITS.shapesPerLesson} shape objects per lesson, glow in at most ${SHAPE_LIMITS.glowColoursPerShape} colours per shape and ${SHAPE_LIMITS.glowColoursPerLesson} per lesson.`,
            'In VR a shape object can also have `rot` (degrees, turned about its `pos`), `scale` (0.1–10) and `hidden` (true: not there until a step shows it); steps can `show`, `hide`, `move` and `highlight` it (Interactions). A shape is one piece: to move parts apart, make each part its own shape object.',
            'The name floats above the shape. Hotspots, `focus` and `view.look` work on a shape as on any object; in AR a shape can be an `assemble` part. `spin` turns the whole shape; `radius`, `ring` and `tilt` do nothing on it.',
        ].join(' '),
    },
    {
        namespace: 'poster',
        names: ['photo'],
        // VR only: an AR lesson is the classroom itself (posters spec).
        modes: ['vr'],
        description: [
            'A framed photo hanging in the air. Use it where seeing the real thing matters: a real place (a volcano, a city), a historical object or artwork, a living organism, a material. Use a shape instead when the pupil must see inside, around or apart, or watch it change. A photo shows what it looks like; a shape shows how it works.',
            "Write `want` (Dutch: what the photo should show, up to 200 characters) and `search` (1–6 English search words); the platform finds the photo. Never write `media`, `aspect` or `credit`: the platform fills those in. `width` is the photo's width in metres (0.4–3; its height follows the photo), `pos` its middle, `facing` optional degrees about the vertical axis (0 faces +z; absent, it faces the pupil); `caption` a short Dutch caption (up to 80 characters).",
            'Prefer `view: {look: id}`: the engine stands the pupil in front of it. At most 4 per lesson; no people who could be recognised: ask for places, objects and organisms. It does not move, turn or scale; steps can `show` and `hide` it.',
        ].join(' '),
    },
    {
        namespace: 'station',
        names: 'any',
        modes: ['vr'],
        // Drawn by the headset, but only a campus tour would use it.
        generatable: false,
        description: 'A plain marker post for a tour stop. Only useful with a campus tour, which is not built yet.',
    },
    {
        namespace: 'model',
        names: 'any',
        // VR only: a realistic model at real size stands in a world (ADR-043).
        modes: ['vr'],
        // Generatable only for the ready library slugs a run is given
        // (isGeneratableKind's `library`, plan C2), and described only to a
        // prompt that offers models (catalogueAsMarkdown's `models`).
        library: true,
        description: [
            'A realistic 3D model from the platform library at its real size: only a model the brief lists under "Modellen in de les", by its slug (`model:t-rex`), and only the animations listed with it.',
            '`pos` is [x, 0, z] on the ground; `turn` is degrees about the vertical axis, and unlike a shape 0 faces away from the pupil (−z): 180 faces the pupil from straight ahead, 90 turns it to its left. No `rot`, `scale`, `parent`, `orbit` or `move` (it walks), and no `size`: it stays at its real size.',
            libraryModelPlacement(STAGE),
            'Steps make it walk, face the pupil and play (`models`, Interactions).',
        ].join(' '),
    },
];
/**
 * Show, hide, move and highlight: to a teacher one activity, "Zien
 * veranderen", listed once on "Wat kan een les?".
 */
const SEE_IT_CHANGE = {
    activity: 'change',
    title: 'Zien veranderen',
    text: 'Iets verschijnt, beweegt, gaat uit elkaar of licht op, precies op het moment dat het iets laat zien.',
};
export const INTERACTIONS = [
    {
        id: 'predict',
        modes: ['vr', 'ar'],
        description: 'Before something is shown, the pupil chooses what they expect from 2–4 options. The step’s `panel` is the introduction, read before the question; the task or the next step shows the answer. Not marked right or wrong.',
        teacher: {
            activity: 'predict',
            title: 'Voorspellen',
            text: 'Voordat iets te zien is, kiest de leerling wat hij verwacht. Daarna ziet of hoort hij het antwoord.',
        },
    },
    {
        id: 'quiz',
        modes: ['vr', 'ar'],
        description: 'A question with 2–4 options and one right answer, with an `explain` shown after answering. The answer is sent to the teacher.',
        teacher: {
            activity: 'quiz',
            title: 'Quizvraag',
            text: 'Een vraag met twee tot vier antwoorden en uitleg erna. In een les ziet u de antwoorden live.',
        },
    },
    {
        id: 'hotspots',
        modes: ['vr', 'ar'],
        description: 'Objects the pupil can point at to open a short text (≤ 200 characters) on the panel. For exploring, not for testing.',
        teacher: {
            activity: 'hotspots',
            title: 'Aanwijzen',
            text: 'De leerling wijst onderdelen aan en leest er kort over.',
        },
    },
    {
        id: 'task:measure',
        modes: ['ar'],
        description: 'The pupil measures something real in the classroom with the headset, after estimating it. Needs a real object to measure.',
        teacher: {
            activity: 'measure',
            title: 'Schatten en meten (AR)',
            text: 'De leerling schat iets in de klas en meet het dan met de headset.',
        },
    },
    {
        id: 'task:assemble',
        // validate.ts refuses measure and assemble in VR.
        modes: ['ar'],
        description: 'The pupil puts parts where they belong: each listed object starts on a tray and snaps into its `pos` within `snapMetres` (default 0.08). Reports how many went in, the misses and the hardest part.',
        teacher: {
            activity: 'assemble',
            title: 'In elkaar zetten (AR)',
            text: 'De leerling zet onderdelen op hun plek, zoals de botten van een skelet.',
        },
    },
    {
        id: 'task:furnish',
        // ADR-037, ADR-050: only in a world with a placement space, with the school's werkstukken;
        // validate.ts needs a vr lesson.
        modes: ['vr'],
        generatable: false,
        description: "The pupils place work they designed themselves (furniture, sculptures, cars) in a world with a placement space: grab a piece, carry it, turn it, colour it. Only with the school's werkstukken.",
    },
    {
        id: 'show',
        modes: ['vr'],
        description: `\`show: [id, …]\` (up to ${MOTION_LIMITS.showPerStep}): objects that appear when the step starts, growing into place. For the reveal after a \`predict\`: the answer appears.`,
        teacher: SEE_IT_CHANGE,
    },
    {
        id: 'hide',
        modes: ['vr'],
        description: `\`hide: [id, …]\` (up to ${MOTION_LIMITS.hidePerStep}): objects that shrink away when the step starts. To clear the view, or to take the model away before a question answered from memory.`,
        teacher: SEE_IT_CHANGE,
    },
    {
        id: 'move',
        modes: ['vr'],
        description: `\`move: [{object, pos?, rot?, scale?, seconds?}]\`: a shape with a \`pos\` moves, turns or grows to where the move says over \`seconds\` (${MOTION_LIMITS.secondsMin}–${MOTION_LIMITS.secondsMax}, default ${MOTION_LIMITS.secondsDefault}), after show and hide. Up to ${MOTION_LIMITS.movesPerStep} a step and ${MOTION_LIMITS.movesPerLesson} a lesson; a step's changes are done within ${MOTION_LIMITS.stepSeconds} s. For what changes: parts flying apart, something growing, magma rising. To make something disappear, \`hide\` it; scale stays 0.1–10.`,
        teacher: SEE_IT_CHANGE,
    },
    {
        id: 'highlight',
        modes: ['vr'],
        description: '`highlight: id`: once the step’s changes are done the object lights up twice and its name shows (a part that glows glows brighter). To say "this one" about a part. What a step is about (`focus`, `view.look`, `highlight`, hotspots) must be there at the end of the step.',
        teacher: SEE_IT_CHANGE,
    },
];
/**
 * A step's spoken words (ADR-033): what the pupil hears on "Voorlezen". The
 * platform renders the audio into `voiceover` when the lesson is sent, so a
 * model writes the words at most, never the file.
 */
export const NARRATION_LINE = '- `narration` (VR/AR): what the voice says for the step, 15–45 s spoken (200–600 characters). Write it for every step: warm, spoken Dutch, sentences of at most 15 words, numbers and abbreviations written out. On a `predict` or `quiz` step, end with its question word for word. Never tell the pupil to press a button: the voice may lead the lesson by itself. Never `voiceover`: we make the audio.';
/** `narration` as a teacher sees it: an activity of every step, VR and AR. */
export const NARRATION_TEACHER = {
    activity: 'narration',
    title: 'Voorlezen en stembegeleiding',
    text: 'Een stem leest elke stap voor; u kiest in de les tussen tekst en stem.',
};
/** How to use change within a lesson for a lesson that impresses and teaches (docs/10: see it change). */
export const MAKING_IT_IMPRESSIVE = [
    'Show the change, don’t describe it: after a `predict`, the next step reveals the answer with `show` or `move`, and the panel names what the pupil sees change ("Kijk: …").',
    'Take things apart to show the inside: build the model from separate shape objects and `move` them apart in one step; bring them back later if the lesson needs the whole again.',
    'One change per step, 1–4 seconds; never motion for decoration.',
    '`glow` for what is hot or gives light, `gloss` for what is wet or polished, sparingly.',
    '`hide` the model before the final question, so the answer comes from memory.',
];
/** Whether the headset draws `kind` as itself rather than as a placeholder. */
export function isBuildableKind(kind) {
    const [namespace, name = ''] = kind.split(':');
    const entry = OBJECT_KINDS.find((candidate) => candidate.namespace === namespace);
    if (!entry)
        return false;
    return entry.names === 'any' || entry.names.includes(name);
}
/**
 * Whether a generated lesson may use `kind`: the headset builds it and the
 * catalogue offers it. A library kind (`model:<slug>`, ADR-043) only when
 * `library` holds its slug: the ready, unretired slugs the run may use.
 */
export function isGeneratableKind(kind, library) {
    const [namespace] = kind.split(':');
    const entry = OBJECT_KINDS.find((candidate) => candidate.namespace === namespace);
    if (!isBuildableKind(kind) || entry?.generatable === false)
        return false;
    if (entry?.library !== true)
        return true;
    const slug = modelSlugOf(kind);
    return slug !== undefined && MODEL_SLUG.test(slug) && library?.has(slug) === true;
}
function sceneFor(id) {
    const [type = '', detail] = id.split(':');
    if (type === 'world')
        return { type: 'world', world: detail };
    if (type === 'procedural')
        return { type: 'procedural', preset: detail };
    return { type };
}
/** Every scene a generated lesson can use, in the catalogue's order. */
export function sceneChoices(worlds = CODE_WORLD_CATALOGUE) {
    return scenesOf(worlds)
        .filter((scene) => scene.generatable)
        .map((scene) => {
        const slug = scene.id.split(':').at(-1) ?? scene.id;
        const world = scene.id.startsWith('world:') ? worldIn(worlds, slug) : undefined;
        return {
            id: scene.id,
            slug,
            ...(world ? { name: world.name.nl } : {}),
            mode: scene.mode,
            scene: sceneFor(scene.id),
        };
    });
}
/** The code worlds' choices: SCENE_CHOICES as it was before packages. */
export const SCENE_CHOICES = sceneChoices();
/** The scene a design names, by slug, scene id or a world's Dutch name, in any case. */
export function sceneChoice(named, worlds = CODE_WORLD_CATALOGUE) {
    const wanted = named.trim().toLowerCase();
    const choices = sceneChoices(worlds);
    // A slug or id is exact and wins; a world's Dutch name is only the second look.
    return (choices.find((choice) => choice.slug === wanted || choice.id === wanted) ??
        choices.find((choice) => choice.name?.toLowerCase() === wanted));
}
/**
 * A lesson's scene as the catalogue names it -- `world:forest`,
 * `procedural:solar-system`, `studio` -- read defensively, since a model's
 * answer may not have passed the schema. Undefined without a `type`.
 */
export function sceneIdOf(scene) {
    if (typeof scene !== 'object' || scene === null)
        return undefined;
    const { type, preset, world } = scene;
    if (typeof type !== 'string')
        return undefined;
    const detail = typeof preset === 'string'
        ? preset
        : type === 'world' && typeof world === 'string'
            ? world
            : undefined;
    return detail === undefined ? type : `${type}:${detail}`;
}
/** A world's features: `wereld:vulkaan` rustig/rokend/uitbarsting; with `detail`, its name and first state. */
function featureLine(id, detail, worlds) {
    const world = id.startsWith('world:') ? worldIn(worlds, id.slice('world:'.length)) : undefined;
    const listed = Object.entries(world?.features ?? {}).map(([name, feature]) => detail
        ? `\`wereld:${name}\` (${feature.name}) ${feature.states.join('/')}, \`${feature.initial}\` at the start`
        : `\`wereld:${name}\` ${feature.states.join('/')}`);
    return listed.length > 0 ? ` Features: ${listed.join('; ')}.` : '';
}
/** Where a route comes nearest, in the prompt's words: "straight ahead", "ahead to the left", "behind". */
function bearingWords(azimuth) {
    const side = azimuth < 0 ? 'left' : 'right';
    if (Math.abs(azimuth) < 7.5)
        return 'straight ahead';
    if (Math.abs(azimuth) <= 35)
        return `ahead to the ${side}`;
    if (Math.abs(azimuth) <= 135)
        return `to the ${side}`;
    return 'behind';
}
/**
 * Code prompt v8 (ADR-069; Pieter, 16 Oct 2026, after "Op Mars": a lesson started the world's own
 * rover instead of the library's marsrover): one line per feature of the world that sets a route
 * going -- it is the world's background, not a model, where it drives, and never the subject when a
 * library model of its kind is chosen. "Wereld is geen model" holds the lesson to it.
 */
function moverLines(world) {
    return (world?.movers ?? []).map((mover) => mover.onderwerp === true
        ? subjectLine(mover)
        : `- \`wereld:${mover.feature}\` (${mover.name}) is part of the world's background, not a model: it moves along its own route, ${n(mover.nearest)} to ${n(mover.farthest)} m from the platform (nearest ${bearingWords(mover.azimuth)}), outside where models stand. Never make it the lesson's subject when a library model of its kind is chosen (a \`model:\` that is the same thing): show and move the model instead, and leave \`wereld:${mover.feature}\` as it is.`);
}
/**
 * A feature marked as the world's own subject (`onderwerp`, ADR-069 amended 20 Oct 2026: the
 * bloodstream of a world about blood): what a lesson may centre on, with the state that sets it going.
 */
function subjectLine(mover) {
    const state = mover.movingStates[0];
    const start = state === undefined ? '' : ` start it moving (\`world: {"${mover.feature}": "${state}"}\`) and`;
    return `- \`wereld:${mover.feature}\` (${mover.name}) is the world's own subject, not its background: the lesson may centre on it,${start} look at it (\`"look": "wereld:${mover.feature}"\`). It moves along its own route, ${n(mover.nearest)} to ${n(mover.farthest)} m from the platform (nearest ${bearingWords(mover.azimuth)}); the platform keeps models off its route. It is not a library model: do not place a \`model:\` of the same thing beside it.`;
}
/** A published package's world (not a code world), as the whole catalogue lists it. */
const isPackageWorld = (id, worlds) => id.startsWith('world:') && worldIn(worlds, id.slice('world:'.length))?.source === 'pakket';
const MODE = (scene) => `(${scene.mode.toUpperCase()})`;
/**
 * Where a chosen world's ground is open (v7, ADR-061): how far from the middle, in eight directions,
 * before scenery below 3 m (`openSpace`); nothing for a world whose scenery is not known.
 */
function openSpaceLine(world, stage) {
    // A package measured before closer worlds has props and moving parts only: its trees are unknown,
    // and unknown ground is not open ground (worlds:scenery fills it).
    if (world?.scenery === undefined || world.sceneryComplete === false)
        return undefined;
    const parts = openSpace(world.scenery).map((o) => `${o.direction} ${o.distance >= 12 ? '12 m or more' : `${n(o.distance)} m`}`);
    return `Open ground round the platform, free of scenery below 3 m: ${parts.join(', ')}. Put every object there, within ${n(stage.radius)} m.`;
}
/** The Scenes section, as `scenes` asks; with `openSpace`, a chosen world's open ground. */
function scenesSection(scenes, worlds, stage, withOpenSpace = false, withMovers = false, withPlacePoints = false) {
    const all = scenesOf(worlds);
    if (scenes.scenes === 'choose') {
        const choosable = sceneChoices(worlds).map((choice) => {
            const scene = all.find((entry) => entry.id === choice.id);
            const name = choice.name === undefined ? '' : ` — ${choice.name}`;
            return `- \`${choice.slug}\`${name} ${MODE(scene)}: ${scene.summary ?? scene.description}${featureLine(scene.id, false, worlds)}`;
        });
        const unavailable = all.filter((scene) => !scene.generatable).map((s) => `\`${s.id}\``);
        return [
            '### Scenes',
            'Choose one for the lesson and write its name on the design’s `Wereld:` line. Its details come with the next step, when the lesson is built.',
            ...choosable,
            `Not available to a generated lesson, as they need uploaded media: ${unavailable.join(', ')}.`,
            '',
            [worldStage(stage), WORLD_CHOICE, WORLD_FEATURES].join(' '),
        ];
    }
    const chosen = scenes.scenes === 'chosen' || scenes.scenes === 'given'
        ? sceneChoice(scenes.slug, worlds)
        : undefined;
    if (chosen?.id === 'pano360') {
        // No one fixed `scene` object: its photo is the lesson's to ask for.
        const scene = all.find((entry) => entry.id === chosen.id);
        if (withPlacePoints) {
            // An echte_plek lesson (plan 2): the brief's place, its photo the platform's to fill.
            return [
                '### Scenes',
                'The approved design chose this scene, the 360° place from the brief.',
                `- \`${scene.id}\` ${MODE(scene)}: ${scene.description}`,
                '',
                `${worldStage(stage).replace(SCENERY_UNPOINTED, '')} Where things go, as in the studio: ${STUDIO_RULES}`,
                '',
                PLACE_POINTS_PARAGRAPH,
            ];
        }
        return [
            '### Scenes',
            'The approved design chose this scene; the lesson’s `scene` is `{"type": "pano360"}` with `want` and `search`, or `brief`.',
            `- \`${scene.id}\` ${MODE(scene)}: ${scene.description}`,
            '',
            `${worldStage(stage)} Where things go, as in the studio: ${STUDIO_RULES}`,
            '',
            PANO_PARAGRAPH,
        ];
    }
    if (chosen) {
        const scene = all.find((entry) => entry.id === chosen.id);
        const isWorld = chosen.scene.type === 'world';
        const name = chosen.name === undefined ? '' : ` — ${chosen.name}`;
        const open = withOpenSpace && chosen.scene.type === 'world'
            ? openSpaceLine(worldIn(worlds, chosen.scene.world), stage)
            : undefined;
        return [
            '### Scenes',
            scenes.scenes === 'given'
                ? `The teacher chose this place: write \`Wereld: ${chosen.slug}\` on the design’s line.`
                : `The approved design chose this scene; the lesson’s \`scene\` is \`${JSON.stringify(chosen.scene)}\`.`,
            `- \`${scene.id}\`${name} ${MODE(scene)}: ${scene.description}${featureLine(scene.id, true, worlds)}`,
            ...(withMovers && chosen.scene.type === 'world'
                ? moverLines(worldIn(worlds, chosen.scene.world))
                : []),
            // The studio's description holds its rules already; AR has neither
            // framing nor lectern checked, so it gets none.
            ...(isWorld
                ? [
                    '',
                    `${worldStage(stage)} Where things go, as in the studio: ${STUDIO_RULES} ${WORLD_FEATURES}`,
                    ...(open === undefined ? [] : [open]),
                ]
                : chosen.id !== 'studio' && chosen.mode === 'vr'
                    ? ['', `Where things go: ${VIEW_RULES}`]
                    : []),
        ];
    }
    const packages = all.filter((scene) => scene.generatable && isPackageWorld(scene.id, worlds));
    const built = all.filter((scene) => !packages.includes(scene));
    return [
        '### Scenes',
        ...built.map((scene) => `- \`${scene.id}\` ${MODE(scene)}${scene.generatable ? '' : ' — not available to a generated lesson'}: ${scene.description}${featureLine(scene.id, false, worlds)}`),
        // A design that named no scene may choose any world, and thirty packages leave no room for their
        // descriptions beside the rest (the prompts stay under 85,000 characters): only their names here.
        ...(packages.length === 0
            ? []
            : [
                `- Worlds from the platform's library (VR; each a \`scene.world\`, its details come with the design that names it): ${packages
                    .map((scene) => `\`${scene.id.slice('world:'.length)}\``)
                    .join(', ')}.`,
            ]),
        '',
        worldsParagraph(stage),
        ...(all.some((scene) => scene.id === 'pano360' && scene.generatable)
            ? ['', PANO_PARAGRAPH]
            : []),
    ];
}
/** The object kinds a Les maken lesson is made of (ADR-053). */
export const LES_MAKEN_KINDS = ['model', 'shape'];
/** The catalogue as a prompt section; all of it unless `scenes` says otherwise. */
export function catalogueAsMarkdown(scenes = { scenes: 'all' }, options = {}) {
    const stage = options.stage ?? STAGE;
    const objects = OBJECT_KINDS.filter((kind) => kind.generatable !== false &&
        (kind.library !== true || options.models === true) &&
        (options.lesMaken !== true || LES_MAKEN_KINDS.includes(kind.namespace))).map((kind) => {
        if (kind.library === true) {
            const description = kind.description.replace(libraryModelPlacement(STAGE), libraryModelPlacement(stage));
            return `- \`${kind.namespace}:<slug>\` (${kind.modes.join('/').toUpperCase()}; a slug from the brief): ${description}`;
        }
        const names = kind.names === 'any' ? 'any name' : kind.names.map((name) => `\`${name}\``).join(', ');
        const description = kind.namespace === 'shape' && options.shapePartsCap === true
            ? `${kind.description} ${SHAPE_PARTS_CAP_LINE}`
            : kind.description;
        return `- \`${kind.namespace}:<name>\` (${kind.modes.join('/').toUpperCase()}; ${names}): ${description}`;
    });
    const interactions = INTERACTIONS.filter((interaction) => interaction.generatable !== false &&
        (options.lesMaken !== true || !interaction.id.startsWith('task:'))).map((interaction) => `- \`${interaction.id}\` (${interaction.modes.join('/').toUpperCase()}): ${interaction.description}`);
    return [
        'Only what is listed here exists. Anything else needs new engine work first; say so rather than inventing it.',
        '',
        ...scenesSection(scenes, options.worlds ?? CODE_WORLD_CATALOGUE, stage, options.openSpace === true, options.movers === true, options.placePoints === true),
        '',
        '### Objects (`kind` is `namespace:name`)',
        ...objects,
        '',
        '### Interactions',
        ...interactions,
        NARRATION_LINE,
        ...(options.impressive === false
            ? []
            : ['', '### Making it impressive', ...MAKING_IT_IMPRESSIVE.map((line) => `- ${line}`)]),
    ].join('\n');
}
/**
 * A place "Wat kan een les?" shows a picture of (ADR-039, item 1):
 * `apps/console/web/plaatsen/<slug>.jpg`, rendered by `pnpm
 * worlds:thumbnails`. Every VR place, the villa included; AR shows the pupil's
 * own classroom, so the console draws an icon for it instead.
 */
export function pictured(place) {
    return place.mode === 'vr';
}
/**
 * What a lesson can be, in a teacher's words (ADR-039, item 1): served by
 * `GET /v1/lesson-briefs/possibilities`. Places: every scene a brief can ask
 * for -- the studio first, then space, the worlds and the 360° photo, AR
 * after the VR places -- and the villa last, not generatable. Activities:
 * every generatable interaction's, each once, with the modes of all the
 * interactions it covers -- those of both modes first, then VR's, then AR's --
 * and narration, on every step, last.
 */
export function teacherPossibilities(worlds = CODE_WORLD_CATALOGUE) {
    const scenes = scenesOf(worlds);
    const choices = sceneChoices(worlds);
    const words = (id) => {
        const teacher = scenes.find((scene) => scene.id === id)?.teacher;
        if (!teacher)
            throw new Error(`scene ${id} has no teacher words`);
        return teacher;
    };
    const place = (slug, id, mode, generatable, picture) => {
        const { title, text } = words(id);
        return { slug, name: title, mode, text, generatable, picture };
    };
    // A world's own picture; any other VR place `plaatsen/<slug>.jpg`; AR none.
    const pictureOf = (choice) => {
        if (choice.scene.type === 'world')
            return worldIn(worlds, choice.slug)?.picture ?? null;
        return pictured({ mode: choice.mode }) ? `plaatsen/${choice.slug}.jpg` : null;
    };
    const first = ['studio', 'solar-system'];
    const ordered = [
        ...first.flatMap((slug) => choices.filter((choice) => choice.slug === slug)),
        ...choices.filter((choice) => choice.mode === 'vr' && !first.includes(choice.slug)),
        ...choices.filter((choice) => choice.mode === 'ar'),
    ];
    const places = [
        ...ordered.map((choice) => place(choice.slug, choice.id, choice.mode, true, pictureOf(choice))),
        // The worlds for placing work, last: not for a generated lesson.
        ...worlds
            .filter((world) => !world.generatable)
            .map((world) => place(world.slug, `world:${world.slug}`, 'vr', false, world.picture)),
    ];
    const activities = new Map();
    const generatable = INTERACTIONS.filter((entry) => entry.generatable !== false);
    for (const entry of generatable) {
        const teacher = entry.teacher;
        if (!teacher)
            throw new Error(`interaction ${entry.id} has no teacher words`);
        const known = activities.get(teacher.activity);
        if (known) {
            for (const mode of entry.modes)
                if (!known.modes.includes(mode))
                    known.modes.push(mode);
        }
        else {
            activities.set(teacher.activity, {
                id: teacher.activity,
                title: teacher.title,
                text: teacher.text,
                modes: [...entry.modes],
            });
        }
    }
    // Those for every lesson first, then VR's, then AR's: the order a teacher reads them in.
    const group = (modes) => modes.length > 1 ? 0 : modes[0] === 'vr' ? 1 : 2;
    return {
        places,
        activities: [
            ...[...activities.values()].sort((a, b) => group(a.modes) - group(b.modes)),
            {
                id: NARRATION_TEACHER.activity,
                title: NARRATION_TEACHER.title,
                text: NARRATION_TEACHER.text,
                modes: ['vr', 'ar'],
            },
        ],
    };
}
//# sourceMappingURL=catalogue.js.map